import React, { memo, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { InputLabel, TextField } from '@picsio/ui';

import CategoriesDropdown from './categoriesDropdown';
import formatCategoryLabel from './helpers/formatCategoryLabel';
import Tooltip from '../../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from '../helpers/ReadOnlyFieldTooltip';
import { productFieldInputProps } from '../constants';
import parentStyles from '../styles.module.scss';
import styles from './styles.module.scss';

const Category = ({
  value,
  readOnly,
  readOnlyTooltip,
  tooltip,
  onChange,
  isDiff,
  disabled,
  filterText,
  placeholder,
}) => {
  const checkedItems = useMemo(() => {
    if (!value?._id) return [];

    return [{
      _id: value._id,
      title: formatCategoryLabel(value),
      name: value.name,
      fullName: value.fullName,
    }];
  }, [value]);

  const handleSelect = useCallback((category) => {
    onChange?.(category);
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange?.(null);
  }, [onChange]);

  const label = (
    <Tooltip content={isDiff ? 'Unpublished changes' : null} placement="top">
      <span className={isDiff ? parentStyles.diffDot : undefined}>Category</span>
    </Tooltip>
  );

  const displayValue = value?._id ? formatCategoryLabel(value) : '';

  return (
    <ReadOnlyFieldTooltip tooltip={tooltip || (readOnly ? readOnlyTooltip : null)}>
      <Choose>
        <When condition={readOnly}>
          <div className={parentStyles.row}>
            <TextField
              value={displayValue}
              disabled={readOnly}
              label={label}
              placeholder={placeholder}
              inputProps={productFieldInputProps}
            />
          </div>
        </When>
        <Otherwise>
          <div className={styles.wrapper}>
            <InputLabel position="top" margin>
              {label}
            </InputLabel>
            <CategoriesDropdown
              checkedItems={checkedItems}
              disabled={disabled}
              filterText={filterText}
              onSelect={handleSelect}
              onClear={handleClear}
            />
          </div>
        </Otherwise>
      </Choose>
    </ReadOnlyFieldTooltip>
  );
};

Category.propTypes = {
  value: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    fullName: PropTypes.string,
  }),
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  tooltip: PropTypes.string,
  isDiff: PropTypes.bool,
  disabled: PropTypes.bool,
  filterText: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
};

Category.defaultProps = {
  value: null,
  readOnly: false,
  readOnlyTooltip: null,
  tooltip: null,
  isDiff: false,
  disabled: false,
  filterText: null,
  placeholder: null,
  onChange: null,
};

export default memo(Category);
